import React, { Fragment } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { keys, partition } from 'lodash-es';

import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  useMenuProps,
} from '../../../../components/DropdownSet';
import Tooltip from '../../../../components/Tooltip';

import {
  ADVANCED_METRICS_APP,
  ADVANCED_METRICS_APP_DESCRIPTION,
  COHORTS,
} from '../../../../utils/advanced-metrics';

const getMetricTooltipText = (metric, cohort) => {
  const description = ADVANCED_METRICS_APP_DESCRIPTION[metric] || '';
  const segment = (COHORTS[cohort] || 'Segment').replace('Users', '');

  return description.replace(/\[Segment\]/g, segment);
};

const AdvancedMetricTooltip = ({ title, titleIcon, description, children }) => (
  <Tooltip
    colorVariant="light"
    content={
      <div className="tooltip-content">
        <p className="tooltip-content__title">
          {titleIcon}
          <span>{title}</span>
        </p>
        <p className="tooltip-content__description">{description}</p>
      </div>
    }
    overlayClassName="tooltip--advanced-metric"
  >
    {children}
  </Tooltip>
);

AdvancedMetricTooltip.propTypes = {
  title: T.string.isRequired,
  titleIcon: T.node,
  description: T.string.isRequired,
  children: T.node.isRequired,
};

export const AdvancedKeyMetricTooltip = ({ metric }) => (
  <AdvancedMetricTooltip
    title="Key Metric"
    titleIcon={<span className="hicon-crown" />}
    description={`${metric} is a key metric for this segment, and has a strong correlation to overall app performance.`}
  >
    <span className="hicon-crown" />
  </AdvancedMetricTooltip>
);

AdvancedKeyMetricTooltip.propTypes = {
  metric: T.string.isRequired,
};

const AdvancedMetricDropdown = ({
  selected,
  keyMetric,
  cohort,
  excludedMetrics,
  disabledMetrics,
  disabled,
  onChange,
}) => {
  const menuProps = useMenuProps({
    menuStyle: { width: 252 },
    className: 'advanced-metrics-menu',
  });

  const [keyMetrics, restMetrics] = partition(keys(ADVANCED_METRICS_APP), (m) => m === keyMetric);
  const sortedMetrics = keyMetrics.concat(restMetrics);

  const options = sortedMetrics.reduce((result, key) => {
    if (excludedMetrics?.includes(key)) return result;

    const label = ADVANCED_METRICS_APP[key];
    const isKeyMetric = key === keyMetric;

    return result.concat({
      value: key,
      label: isKeyMetric ? (
        <div className="key-metric">
          <span className="hicon-crown" />
          <div className="d-flex flex-column label-text-box">
            <span className="title">{label}</span>
            <span className="subtitle">
              This is a key metric for this segment, and has a strong correlation to overall app
              performance.
            </span>
          </div>
        </div>
      ) : (
        label
      ),
      buttonLabel: (
        <>
          {isKeyMetric && <AdvancedKeyMetricTooltip metric={label} />}
          <span className="title text-truncate">{label}</span>
          <AdvancedMetricTooltip title={label} description={getMetricTooltipText(key, cohort)}>
            <span className="hicon-question-circle" />
          </AdvancedMetricTooltip>
        </>
      ),
      disabled: disabledMetrics?.includes(key),
      isKeyMetric,
    });
  }, []);

  const selectedOption = options.find((option) => option.value === selected) || {};

  const handleChange = ({ value }) => {
    onChange(value);
  };

  return (
    <div className="advanced-metrics-menu-container">
      <Menu
        menuButton={
          <MenuButton disabled={disabled} frameless>
            {selectedOption.buttonLabel || selectedOption.label}
          </MenuButton>
        }
        {...menuProps}
      >
        {options.map(
          (
            {
              label,
              value,
              disabled: optionDisabled = false,
              isKeyMetric,
              className: optionClassName,
            },
            index
          ) => (
            <Fragment key={value || `menu-item-${index}`}>
              <MenuItem
                value={value}
                disabled={optionDisabled}
                className={cx({ active: selectedOption.value === value }, optionClassName)}
                onClick={handleChange}
              >
                {label}
              </MenuItem>
              {isKeyMetric && <MenuDivider />}
            </Fragment>
          )
        )}
      </Menu>
    </div>
  );
};

AdvancedMetricDropdown.propTypes = {
  selected: T.string,
  keyMetric: T.string,
  cohort: T.string,
  excludedMetrics: T.array,
  disabledMetrics: T.array,
  disabled: T.bool,
  onChange: T.func.isRequired,
};

export default AdvancedMetricDropdown;
